<template>
  <div class="inner-drawer-list">
    <v-list v-if="routes && Array.isArray(routes) && dictionariesLoaded">
      <div
        v-for="item in routes.filter(item => !item.hidden)"
        :key="item.name + $route.params.id"
      >
        <v-list-item
          v-if="isVisibleItem(item)"
          :to="resolvePath(item)"
          ripple="ripple"
        >
          <v-list-item-icon class="inner-drawer-list__icon">
            <v-icon color="accent">{{ getListIcon(item) }}</v-icon>
          </v-list-item-icon>

          <v-list-item-content>
            <v-list-item-title>
              {{ getListTitle(item) }}
            </v-list-item-title>
          </v-list-item-content>

          <div class="v-list-item__after">&nbsp;</div>
        </v-list-item>
      </div>
    </v-list>
  </div>
</template>

<script>
import { resolve } from 'path'
import { mapGetters, mapState } from 'vuex'
import Config from '@/config.json'

export default {
  name: 'InnerDrawerList',
  props: {
    isNest: Boolean,
    routes: {
      type: Array,
      default: () => {},
    },
    basePath: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapGetters('userStates', ['getRolename']),
    ...mapState('bhUsersStates', ['bhUser']),
    ...mapState('dictionariesStates', [
      'dictionariesLoaded',
      'dictionaryPracticeUserRole',
      'dictionaryBhUsersRoleIds',
      'dictionaryFeatureFlagState',
    ]),
    ...mapState('patientsStates', ['currentPatient']),
  },
  data: () => ({}),
  methods: {
    isExternal(path) {
      return /^(https?:|mailto:|tel:)/.test(path)
    },
    hideFromUser(item) {
      // Check if features are enabled in config (env variables)
      if (item.enabledFeatures && Array.isArray(item.enabledFeatures)) {
        const featureDisabled = item.enabledFeatures.find(
          feature => Config[feature] !== true && Config[feature] !== 'true',
        )

        // Found at least one disabled feature which is required
        if (featureDisabled) {
          return true
        }
      }

      // would be nice to extend it for accepting arrays of role ids as well
      if (this.bhUser.roleId && this.bhUser.roleId === item.hideOnRole) {
        return true
      }

      // Check if item should be hidden on specific user role
      let hideOnRole = false
      if (item.hideOnRole) {
        if (Array.isArray(item.hideOnRole)) {
          if (
            item.hideOnRole.includes(
              this.dictionaryPracticeUserRole[this.getRolename],
            )
          )
            hideOnRole = true
        } else {
          if (
            item.hideOnRole == this.dictionaryPracticeUserRole[this.getRolename]
          )
            hideOnRole = true
        }
        if (item.hideOnRoleAlsoAdmins) {
          if (Array.isArray(item.hideOnRole)) {
            if (
              item.hideOnRole.includes(
                this.dictionaryBhUsersRoleIds[this.getRolename],
              )
            )
              hideOnRole = true
          } else {
            if (
              item.hideOnRole == this.dictionaryBhUsersRoleIds[this.getRolename]
            )
              hideOnRole = true
          }
        }
      }
      if (hideOnRole === true) return true

      // Check if available feature flag (in this participant) is other than inactive
      if (item.availableParticipantFeatureFlag) {
        if (
          this.currentPatient[item.availableParticipantFeatureFlag] ===
          this.dictionaryFeatureFlagState[
            this.dictionaryFeatureFlagState.Inactive
          ]
        )
          return true
      }

      // Show element
      return false
    },
    isVisibleItem(item) {
      return !this.hideFromUser(item) && !item.alwaysShow
    },
    resolvePath(item) {
      if (this.isExternal(item.path)) {
        return item.path
      }
      return { name: item.name, path: item.path, params: this.$route.params }
      // return resolve(this.basePath, path);
    },
    getListIcon(item) {
      return item.meta && item.meta.icon ? item.meta.icon : ' '
    },
    getListTitle(item) {
      return item.meta && item.meta.menuTitle
        ? item.meta.menuTitle
        : item.meta && item.meta.title
        ? item.meta.title
        : ''
    },
  },
}
</script>
<style lang="scss">
@import '@/styles/variables.scss';

.inner-drawer-list {
  padding-bottom: 0px;
  padding-top: 32px;

  .v-list {
    &-item {
      &::before {
        background-color: rgba(255, 255, 255, 0.6);
        border-top-left-radius: 24px;
        border-bottom-left-radius: 24px;
        left: 16px;
        z-index: 2;
        transition: none;
      }

      &:not(.v-list-item--active):not(.v-list-item--disabled) {
        color: $bh-blue !important;
      }

      &:not(.v-list-item--active):hover {
        z-index: 3;

        &::before {
          opacity: 0.3 !important;
        }
      }

      .v-ripple__container {
        border-top-left-radius: 24px;
        border-bottom-left-radius: 24px;
        left: 16px;
      }

      &--active {
        color: $bh-blue !important;
        padding-right: 0;
        padding-left: 72px;

        &::before {
          opacity: 1 !important;
          left: 56px;
        }

        &::after {
          background-color: $bh-white;
        }

        .v-ripple__container {
          display: none !important;
        }

        .v-list-item__after {
          opacity: 1;
        }

        .v-list-item__icon {
          order: 2;
          width: 48px;
          height: 48px;
          margin: 0 !important;
          background-color: $bh-white;
          align-items: center;
          justify-content: center;
          border-radius: 24px;
        }
      }

      &__after {
        position: absolute;
        right: 0;
        top: -24px;
        height: calc(100% + 48px);
        width: 24px;
        opacity: 0;
        background-color: $bh-white;
        // transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
        z-index: 1;

        &::before,
        &::after {
          content: ' ';
          position: absolute;
          right: 0;
          width: 24px;
          height: 24px;
          background-color: $bh-blue--4;
          border-radius: 0;
        }

        &::before {
          top: 0;
          border-bottom-right-radius: 24px;
        }

        &::after {
          bottom: 0;
          border-top-right-radius: 24px;
        }
      }

      &__icon {
        width: 16px;
        margin: 0 12px 0 6px !important;
        height: 48px;
        text-align: center;
        z-index: 2;
        min-width: 16px;
      }

      &__title {
        font-weight: 700;
        line-height: 1.425;
        z-index: 2;
      }
    }
  }
}
</style>
